import { CronFrequency, parseCronString } from "@/components/flexkit/CronSelector";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * Formats a number with its ordinal suffix (1st, 2nd, 3rd, etc.)
 */
export const formatOrdinal = (n: number): string => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 * Formats hour and minute into 12-hour time format (e.g., "9:00 AM")
 */
export const formatTime12Hour = (hour: number, minute: number): string => {
    const h = hour % 12 || 12;
    const m = minute.toString().padStart(2, "0");
    const ampm = hour < 12 ? "AM" : "PM";
    return `${h}:${m} ${ampm}`;
};

/**
 * Converts a cron expression into a human-readable string.
 * Returns null if the cron expression cannot be parsed.
 *
 * Examples:
 * - "10 * * * *" → "Scheduled hourly at 10 min"
 * - "0 9 * * *" → "Scheduled daily at 9:00 AM UTC"
 * - "30 14 * * 1" → "Scheduled weekly on Monday at 2:30 PM UTC"
 * - "0 8 15 * *" → "Scheduled monthly on the 15th at 8:00 AM UTC"
 */
export const formatCronToReadable = (cronExpression: string): string | null => {
    const config = parseCronString(cronExpression);
    if (!config) return null;

    const { frequency, minute = 0, hour = 0, dayOfMonth = 1, dayOfWeek = 0 } = config;

    switch (frequency) {
        case CronFrequency.Hour:
            return `Scheduled hourly at ${minute} min`;
        case CronFrequency.Day:
            return `Scheduled daily at ${formatTime12Hour(hour, minute)} UTC`;
        case CronFrequency.Week:
            return `Scheduled weekly on ${WEEKDAYS[dayOfWeek]} at ${formatTime12Hour(hour, minute)} UTC`;
        case CronFrequency.Month:
            return `Scheduled monthly on the ${formatOrdinal(dayOfMonth)} at ${formatTime12Hour(hour, minute)} UTC`;
        default:
            return null;
    }
};
